import React, { useState, useEffect } from 'react'
import { useParams, useOutletContext, useNavigate } from 'react-router-dom'
import { portalRequest } from '../../utils/portalApi'
import { 
  ArrowLeft, User, Calendar, Hash, MapPin, Phone, Mail, 
  AlertTriangle, Heart, Edit2, Save, X, Shield
} from 'lucide-react'

export default function PortalPlayerDetail() {
  const { playerId } = useParams()
  const { team, canManage } = useOutletContext()
  const navigate = useNavigate()
  const [player, setPlayer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({})

  useEffect(() => {
    if (playerId) fetchPlayer()
  }, [playerId])

  const fetchPlayer = async () => {
    try {
      const data = await portalRequest(`/api/players/${playerId}/details`)
      setPlayer(data)
      setForm({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        jersey_number: data.jersey_number || '',
        position: data.position || '',
        date_of_birth: data.date_of_birth || '',
        gender: data.gender || '',
        medical_notes: data.medical_notes || '',
        allergies: data.allergies || '',
        emergency_contact_name: data.emergency_contact_name || '',
        emergency_contact_phone: data.emergency_contact_phone || '',
      })
    } catch (err) {
      console.error('Error fetching player:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setError('')
    setSaving(true)
    try {
      await portalRequest(`/api/players/${playerId}`, {
        method: 'PUT',
        body: JSON.stringify(form)
      })
      await fetchPlayer()
      setEditing(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500"></div>
      </div>
    )
  }

  if (!player) {
    return (
      <div className="text-center py-20 text-zinc-400">Player not found</div>
    )
  }

  const InfoRow = ({ icon: Icon, label, value, iconColor = 'text-zinc-500' }) => (
    <div className="flex items-start gap-3 py-3 border-b border-zinc-700/50 last:border-0">
      <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
      <div>
        <p className="text-xs text-zinc-500 font-medium">{label}</p>
        <p className="text-sm text-zinc-200 mt-0.5">{value || '—'}</p>
      </div>
    </div>
  )

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button
        onClick={() => navigate(`/portal/team/${team.team_id}/roster`)}
        className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back to Roster</span>
      </button>

      {/* Player Header */}
      <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden">
              {player.photo ? (
                <img src={player.photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-zinc-400" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {player.first_name} {player.last_name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                {player.jersey_number && (
                  <span className="text-sm text-zinc-400">#{player.jersey_number}</span>
                )}
                {player.position && (
                  <span className="text-sm text-green-400 bg-green-500/10 px-2 py-0.5 rounded">
                    {player.position}
                  </span>
                )}
              </div>
            </div>
          </div>
          {canManage && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-300 rounded-xl text-sm font-medium hover:bg-zinc-700 transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
          )}
        </div>

        {!editing ? (
          <div>
            <InfoRow icon={Hash} label="Jersey Number" value={player.jersey_number} />
            <InfoRow icon={MapPin} label="Position" value={player.position} />
            <InfoRow icon={Calendar} label="Date of Birth" value={player.date_of_birth} />
            <InfoRow icon={User} label="Gender" value={player.gender} />
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="bg-red-500/10 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">First Name</label>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={(e) => update('first_name', e.target.value)}
                  className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Last Name</label>
                <input
                  type="text"
                  value={form.last_name}
                  onChange={(e) => update('last_name', e.target.value)}
                  className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Jersey #</label>
                <input
                  type="text"
                  value={form.jersey_number}
                  onChange={(e) => update('jersey_number', e.target.value)}
                  className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Position</label>
                <input
                  type="text"
                  value={form.position}
                  onChange={(e) => update('position', e.target.value)}
                  className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Date of Birth</label>
                <input
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => update('date_of_birth', e.target.value)}
                  className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Gender</label>
                <select
                  value={form.gender}
                  onChange={(e) => update('gender', e.target.value)}
                  className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                >
                  <option value="">—</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 disabled:opacity-50 transition-all"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="flex items-center gap-2 px-5 py-2.5 border border-zinc-700 text-zinc-300 rounded-xl text-sm font-medium hover:bg-zinc-700 transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Medical & Emergency Info */}
      <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-zinc-400" />
          Medical & Emergency
        </h2>

        {!editing ? (
          <div>
            <InfoRow icon={Heart} label="Medical Notes" value={player.medical_notes} iconColor="text-red-400" />
            <InfoRow icon={AlertTriangle} label="Allergies" value={player.allergies} iconColor="text-amber-400" />
            <InfoRow icon={Phone} label="Emergency Contact" value={
              player.emergency_contact_name 
                ? `${player.emergency_contact_name}${player.emergency_contact_phone ? ' · ' + player.emergency_contact_phone : ''}`
                : null
            } />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Medical Notes</label>
              <textarea
                value={form.medical_notes}
                onChange={(e) => update('medical_notes', e.target.value)}
                rows={2}
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none"
                placeholder="Any medical conditions..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Allergies</label>
              <input
                type="text"
                value={form.allergies}
                onChange={(e) => update('allergies', e.target.value)}
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="Known allergies..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Emergency Contact Name</label>
                <input
                  type="text"
                  value={form.emergency_contact_name}
                  onChange={(e) => update('emergency_contact_name', e.target.value)}
                  className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Emergency Contact Phone</label>
                <input
                  type="tel"
                  value={form.emergency_contact_phone}
                  onChange={(e) => update('emergency_contact_phone', e.target.value)}
                  className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Player Account Info */}
      {(player.player_email || player.has_own_account) && (
        <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Player Account</h2>
          {player.player_email && (
            <InfoRow icon={Mail} label="Player Email" value={player.player_email} />
          )}
          {player.player_phone && (
            <InfoRow icon={Phone} label="Player Phone" value={player.player_phone} />
          )}
          <div className="mt-3">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-lg ${
              player.has_own_account 
                ? 'bg-green-500/10 text-green-400' 
                : 'bg-zinc-700 text-zinc-400'
            }`}>
              {player.has_own_account ? 'Has own account' : 'No player account'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
